import React, { useState, useEffect } from "react";
import {
  DollarSign,
  Package,
  TrendingUp,
  PhoneCall,
  Truck,
  BarChart3,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import LoadingSpinner from "../../components/LoadingSpinner";
import ErrorMessage from "../../components/ErrorMessage";
import { useLanguage } from "../../contexts/LanguageContext";
import type { AnalyticsFilters } from "../../types/analytics";
import {
  fetchAnalyticsRevenue,
  fetchAnalyticsOrdersProducts,
  fetchAnalyticsPerformance,
  fetchAnalyticsCallbacksReasons,
  fetchAnalyticsOutcomes,
  fetchAnalyticsShipping,
} from "./analyticsApi";
import type {
  AnalyticsRevenueResponse,
  AnalyticsOrdersProductsResponse,
  AnalyticsPerformanceResponse,
  AnalyticsCallbacksReasonsResponse,
  AnalyticsOutcomesResponse,
  AnalyticsShippingResponse,
} from "../../types/analytics";

interface InsightsTabProps {
  filters: AnalyticsFilters;
}

const InsightsTab: React.FC<InsightsTabProps> = ({ filters }) => {
  const { t } = useLanguage();
  const [revenue, setRevenue] = useState<AnalyticsRevenueResponse | null>(null);
  const [ordersProducts, setOrdersProducts] = useState<AnalyticsOrdersProductsResponse | null>(null);
  const [performance, setPerformance] = useState<AnalyticsPerformanceResponse | null>(null);
  const [callbacks, setCallbacks] = useState<AnalyticsCallbacksReasonsResponse | null>(null);
  const [outcomes, setOutcomes] = useState<AnalyticsOutcomesResponse | null>(null);
  const [shipping, setShipping] = useState<AnalyticsShippingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchAnalyticsRevenue(filters),
      fetchAnalyticsOrdersProducts(filters),
      fetchAnalyticsPerformance(filters),
      fetchAnalyticsCallbacksReasons(filters),
      fetchAnalyticsOutcomes(filters),
      fetchAnalyticsShipping(filters),
    ])
      .then(([rev, op, perf, cb, out, ship]) => {
        if (!cancelled) {
          setRevenue(rev);
          setOrdersProducts(op);
          setPerformance(perf);
          setCallbacks(cb);
          setOutcomes(out);
          setShipping(ship);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || t("analytics.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [filters.from, filters.to, filters.task, filters.operatorId, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }
  if (error) {
    return <ErrorMessage message={error} onRetry={() => setError(null)} />;
  }

  return (
    <div className="space-y-10">
      {/* Revenue */}
      {revenue && (
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-[#264a3e]" />
            {t("analytics.insights.revenue")}
          </h2>
          <div className="mb-4">
            <span className="text-2xl font-bold text-[#264a3e]">
              {revenue.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })} {revenue.currency}
            </span>
            <span className="text-sm text-gray-500 ml-2">{revenue.from} – {revenue.to}</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2">{t("analytics.insights.revenueByOperator")}</h3>
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">{t("analytics.operator")}</th>
                      <th className="text-right py-2">{t("analytics.insights.revenue")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenue.byOperator.map((row) => (
                      <tr key={row.operatorId} className="border-b border-gray-100">
                        <td className="py-2">{row.username}</td>
                        <td className="text-right py-2 font-medium">
                          {row.revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })} {revenue.currency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2">{t("analytics.insights.revenueByDay")}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={revenue.byDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v}`} />
                  <Tooltip formatter={(v: number) => [`${v.toFixed(2)} ${revenue.currency}`, t("analytics.insights.revenue")]} />
                  <Line type="monotone" dataKey="revenue" stroke="#264a3e" strokeWidth={2} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}

      {/* Orders & Products */}
      {ordersProducts && (
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-[#264a3e]" />
            {t("analytics.insights.ordersAndProducts")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 rounded p-3">
              <span className="text-sm text-gray-600">{t("analytics.insights.ordersCreated")}</span>
              <p className="text-xl font-semibold text-[#264a3e]">{ordersProducts.ordersCreated}</p>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <span className="text-sm text-gray-600">{t("analytics.insights.totalQuantitySold")}</span>
              <p className="text-xl font-semibold text-[#264a3e]">
                {ordersProducts.productBreakdown.reduce((s, p) => s + p.quantity, 0)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2">{t("analytics.insights.productBreakdown")}</h3>
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">{t("analytics.insights.product")}</th>
                      <th className="text-right py-2">{t("analytics.insights.quantity")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordersProducts.productBreakdown.slice(0, 20).map((p) => (
                      <tr key={p.name} className="border-b border-gray-100">
                        <td className="py-2">{p.name}</td>
                        <td className="text-right py-2 font-medium">{p.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2">{t("analytics.insights.byDay")}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={ordersProducts.byDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="ordersCreated" name={t("analytics.insights.orders")} fill="#264a3e" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="quantitySold" name={t("analytics.insights.quantitySold")} fill="#5a7a6e" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}

      {/* Operator Performance */}
      {performance && (
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#264a3e]" />
            {t("analytics.insights.performance")}
          </h2>
          <p className="text-sm text-gray-500 mb-4">{t("analytics.insights.performanceNote")}</p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">{t("analytics.operator")}</th>
                  <th className="text-right py-2">{t("analytics.insights.calls")}</th>
                  <th className="text-right py-2">{t("analytics.insights.avgTimeBetweenCalls")}</th>
                </tr>
              </thead>
              <tbody>
                {performance.rows.map((row) => (
                  <tr key={row.operatorId} className="border-b border-gray-100">
                    <td className="py-2">{row.username}</td>
                    <td className="text-right py-2 font-medium">{row.calls}</td>
                    <td className="text-right py-2">
                      {row.calls >= 2
                        ? `${row.avgTimeBetweenCallsMinutes} min`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Callbacks reasons */}
      {callbacks && (
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
            <PhoneCall className="w-5 h-5 text-[#264a3e]" />
            {t("analytics.insights.callbackReasons")}
          </h2>
          <div className="mb-2 text-sm text-gray-600">
            {t("analytics.insights.totalCallbacksScheduled")}: {callbacks.totalCallbacksScheduled}
          </div>
          <div className="overflow-x-auto max-h-48 overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">{t("analytics.insights.reason")}</th>
                  <th className="text-right py-2">{t("analytics.insights.count")}</th>
                </tr>
              </thead>
              <tbody>
                {callbacks.byReason.map((r) => (
                  <tr key={r.reason} className="border-b border-gray-100">
                    <td className="py-2">{r.reason}</td>
                    <td className="text-right py-2 font-medium">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Outcomes breakdown */}
      {outcomes && (
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-[#264a3e]" />
            {t("analytics.insights.outcomes")}
          </h2>
          <div className="mb-2 text-sm text-gray-600">
            {t("analytics.insights.totalCalls")}: {outcomes.totalCalls}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2">{t("analytics.insights.byOutcome")}</h3>
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">{t("analytics.insights.outcome")}</th>
                      <th className="text-right py-2">{t("analytics.insights.count")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(outcomes.outcomeCounts)
                      .sort(([, a], [, b]) => b - a)
                      .map(([outcome, count]) => (
                        <tr key={outcome} className="border-b border-gray-100">
                          <td className="py-2">{outcome}</td>
                          <td className="text-right py-2 font-medium">{count}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
            {Object.keys(outcomes.notInterestedReasons).length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-2">{t("analytics.insights.notInterestedReasons")}</h3>
                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">{t("analytics.insights.reason")}</th>
                        <th className="text-right py-2">{t("analytics.insights.count")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(outcomes.notInterestedReasons)
                        .sort(([, a], [, b]) => b - a)
                        .map(([reason, count]) => (
                          <tr key={reason} className="border-b border-gray-100">
                            <td className="py-2">{reason}</td>
                            <td className="text-right py-2 font-medium">{count}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Shipping */}
      {shipping && (
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
            <Truck className="w-5 h-5 text-[#264a3e]" />
            {t("analytics.insights.shipping")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded p-4">
              <span className="text-sm text-gray-600">{t("analytics.insights.ordersInPeriod")}</span>
              <p className="text-2xl font-semibold text-[#264a3e]">{shipping.ordersInPeriod}</p>
            </div>
            <div className="bg-green-50 rounded p-4">
              <span className="text-sm text-gray-600">{t("analytics.insights.sentToShippingOrConfirmed")}</span>
              <p className="text-2xl font-semibold text-green-700">{shipping.sentToShippingOrConfirmed}</p>
            </div>
            <div className="bg-amber-50 rounded p-4">
              <span className="text-sm text-gray-600">{t("analytics.insights.waitingConfirmation")}</span>
              <p className="text-2xl font-semibold text-amber-700">{shipping.waitingConfirmation}</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default InsightsTab;
